import { useQueries } from '@tanstack/react-query';
import { WaterLevelObservation } from '../../waterLevel.types';
import { API } from '../../../../api';

type UseWaterLevelObservationsProps = {
  id: number;
  start: Date;
  end: Date;
  pollInterval?: number;
};

type UseWaterLevelObservations = {
  observations?: WaterLevelObservation[];
  forecasts?: WaterLevelObservation[];
  isLoading: boolean;
  isLoadingObservations: boolean;
  isLoadingForecasts: boolean;
  error: Error | null;
  isSuccess: boolean;
  isError: boolean;
};

type ServerWaterLevelObservation = {
  timestamp: string;
  values: {
    flow: number;
    level: number;
  };
};

type FetchObservationsValues = {
  id: string | number;
  start: Date;
  end: Date;
};

type FetchForecastValues = {
  id: string | number;
};

const fetchWaterLevelObservations = async (
  values: FetchObservationsValues,
): Promise<WaterLevelObservation[]> => {
  const { id, start, end } = values;
  const response = await API.get<ServerWaterLevelObservation[]>(
    `weather/observations/${id}/`,
    {
      params: {
        start: start.toISOString(),
        end: end.toISOString(),
      },
    },
  );

  return response.data.map((observation) => ({
    timestamp: new Date(observation.timestamp),
    ...observation.values,
  }));
};

const fetchWaterLevelForecasts = async (
  values: FetchForecastValues,
): Promise<WaterLevelObservation[]> => {
  const { id } = values;
  const response = await API.get<ServerWaterLevelObservation[]>(
    `weather/forecast/${id}/`,
  );

  return response.data.map((observation) => ({
    timestamp: new Date(observation.timestamp),
    ...observation.values,
  }));
};

const removeObservationsFromForecasts = (
  observations: WaterLevelObservation[] | undefined,
  forecasts: WaterLevelObservation[] | undefined,
): WaterLevelObservation[] | undefined => {
  const latestObservation = observations?.[0];

  if (!latestObservation || !forecasts?.length) {
    return forecasts;
  }

  return forecasts.filter(
    ({ timestamp }) => timestamp > latestObservation.timestamp,
  );
};

export const useWaterLevelObservations = (
  props: UseWaterLevelObservationsProps,
): UseWaterLevelObservations => {
  const { id, start, end, pollInterval } = props;

  const observationsQuery = {
    queryKey: ['waterLevelObservations', id],
    queryFn: () => fetchWaterLevelObservations({ id, start, end }),
    refetchInterval: pollInterval ?? undefined,
  };

  const forecastQuery = {
    queryKey: ['waterLevelForecasts', id],
    queryFn: () => fetchWaterLevelForecasts({ id }),
    refetchInterval: pollInterval ?? undefined,
  };

  const [observationsResults, forecastsResults] = useQueries({
    queries: [observationsQuery, forecastQuery],
  });

  const {
    data: observations,
    isLoading: isLoadingObservations,
    error: observationsError,
    isSuccess: isObservationsSuccess,
    isError: isObservationsError,
  } = observationsResults;

  const {
    data: forecasts,
    isLoading: isLoadingForecasts,
    error: forecastsError,
    isSuccess: isForecastsSuccess,
    isError: isForecastsError,
  } = forecastsResults;

  return {
    isLoading: isLoadingObservations || isLoadingForecasts,
    isLoadingObservations,
    isLoadingForecasts,
    error: observationsError || forecastsError,
    observations,
    forecasts: removeObservationsFromForecasts(observations, forecasts),
    isSuccess: isObservationsSuccess && isForecastsSuccess,
    isError: isObservationsError && isForecastsError,
  };
};
